// Adapter between remark-parse's deeply nested mdast AST and the flat ContentBlock[]
// format consumed by downloadProjectFeedbackPdf.ts. remark-parse handles the actual
// markdown parsing; this module walks the resulting AST and flattens it into simple
// heading/paragraph/list blocks with inline TextSegment arrays that the PDF renderer
// can iterate over without dealing with recursive tree traversal.
//
// Supports: H1-H6 headings, paragraphs with inline bold/italic, bullet lists,
// numbered lists, nested lists, inline links, code blocks, tables, horizontal rules,
// and blockquotes.

import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';

// oxlint-disable-next-line quantic/no-type-exports
export type TextSegment = {
    text: string;
    bold?: boolean;
    italic?: boolean;
    link?: string;
    code?: boolean;
};

// oxlint-disable-next-line quantic/no-type-exports
export type ListItem = {
    segments: TextSegment[];
    indent: number;
    index?: number; // 1-based index for ordered lists
};

// oxlint-disable-next-line quantic/no-type-exports
export type TableRow = { cells: TextSegment[][] };

type TableAlignment = 'left' | 'center' | 'right';

// oxlint-disable-next-line quantic/no-type-exports
export type ContentBlock =
    | { type: 'heading'; text: string; depth: number }
    | { type: 'paragraph'; segments: TextSegment[] }
    | { type: 'list'; items: ListItem[]; ordered: boolean }
    | { type: 'codeBlock'; text: string }
    | { type: 'table'; headerRow: TableRow; bodyRows: TableRow[]; alignments: TableAlignment[] }
    | { type: 'horizontalRule' }
    | { type: 'blockquote'; blocks: ContentBlock[] };

// Minimal mdast node types used from remark-parse output
type MdastNode = {
    type: string;
    children?: MdastNode[];
    value?: string;
    depth?: number;
    ordered?: boolean | null;
    start?: number | null;
    url?: string;
    align?: (string | null)[];
};

// Extract plain text from a node tree (used for heading text)
function extractPlainText(node: MdastNode): string {
    if (node.value) return node.value;
    if (node.children) return node.children.map(extractPlainText).join('');
    return '';
}

// Extract inline text segments with formatting from an mdast node tree
function extractSegments(node: MdastNode, inheritBold = false, inheritItalic = false): TextSegment[] {
    if (node.type === 'text') {
        const segment: TextSegment = { text: node.value || '' };
        if (inheritBold) segment.bold = true;
        if (inheritItalic) segment.italic = true;
        return [segment];
    }

    if (node.type === 'strong') {
        return (node.children || []).flatMap(child => extractSegments(child, true, inheritItalic));
    }

    if (node.type === 'emphasis') {
        return (node.children || []).flatMap(child => extractSegments(child, inheritBold, true));
    }

    if (node.type === 'link') {
        const url = node.url || '';
        const linkText = extractPlainText(node);
        const segment: TextSegment = { text: linkText, link: url };
        if (inheritBold) segment.bold = true;
        if (inheritItalic) segment.italic = true;
        return [segment];
    }

    if (node.type === 'inlineCode') {
        const codeSegment: TextSegment = { text: node.value || '', code: true };
        if (inheritBold) codeSegment.bold = true;
        return [codeSegment];
    }

    // For other node types, recurse into children
    if (node.children) {
        return node.children.flatMap(child => extractSegments(child, inheritBold, inheritItalic));
    }

    if (node.value) return [{ text: node.value }];
    return [];
}

// Flatten nested list items with indent levels for rendering
function extractListItems(node: MdastNode, indent: number, ordered: boolean, startIndex: number): ListItem[] {
    const items: ListItem[] = [];
    let index = startIndex;

    (node.children || []).forEach(listItem => {
        if (listItem.type !== 'listItem') return;

        (listItem.children || []).forEach(child => {
            if (child.type === 'paragraph') {
                const item: ListItem = { segments: extractSegments(child), indent };
                if (ordered) item.index = index;
                items.push(item);
            } else if (child.type === 'list') {
                const nestedOrdered = child.ordered === true;
                const nestedStart = child.start ?? 1;
                items.push(...extractListItems(child, indent + 1, nestedOrdered, nestedStart));
            }
        });

        index += 1;
    });

    return items;
}

function extractTableRow(node: MdastNode): TableRow {
    const cells = (node.children || []).map(cell => extractSegments(cell));
    return { cells };
}

function convertTableNode(node: MdastNode): Extract<ContentBlock, { type: 'table' }> {
    const rows = (node.children || []).map(extractTableRow);
    const headerRow = rows[0] || { cells: [] };
    const bodyRows = rows.slice(1);
    const alignments: TableAlignment[] = (node.align || []).map(a => {
        if (a === 'center' || a === 'right') return a;
        return 'left';
    });
    return { type: 'table', headerRow, bodyRows, alignments };
}

function convertNodesToBlocks(nodes: MdastNode[]): ContentBlock[] {
    const blocks: ContentBlock[] = [];

    nodes.forEach(node => {
        switch (node.type) {
            case 'heading': {
                const depth = node.depth ?? 2;
                blocks.push({ type: 'heading', text: extractPlainText(node), depth });
                break;
            }
            case 'paragraph': {
                blocks.push({ type: 'paragraph', segments: extractSegments(node) });
                break;
            }
            case 'list': {
                const ordered = node.ordered === true;
                const startIndex = node.start ?? 1;
                const items = extractListItems(node, 0, ordered, startIndex);
                blocks.push({ type: 'list', items, ordered });
                break;
            }
            case 'code': {
                if (node.value) {
                    blocks.push({ type: 'codeBlock', text: node.value });
                }
                break;
            }
            case 'table': {
                blocks.push(convertTableNode(node));
                break;
            }
            case 'thematicBreak': {
                blocks.push({ type: 'horizontalRule' });
                break;
            }
            case 'blockquote': {
                if (node.children) {
                    blocks.push({ type: 'blockquote', blocks: convertNodesToBlocks(node.children) });
                }
                break;
            }
            default:
                break;
        }
    });

    return blocks;
}

export function parseMarkdownForPdf(markdown: string): ContentBlock[] {
    if (!markdown) return [];

    const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown) as MdastNode;
    return convertNodesToBlocks(tree.children || []);
}
